import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { indexStore } from "src/store/indexStore";
import AddCouponsModel from "./AddCouponsModel";
import Iterator from "../utils/Iterator";
import Visibility from "../utils/Visibility";
import PaginationComponent from "../Pagination/PaginationComponent";
import { DatePicker } from "../DatePicker/DatePicker";
import { format } from "date-fns";
import { Input } from "../ui/input";
import CouponCard, { CouponCardSkeleton } from "../Cards/CouponCard";
import { SelectBox } from "../selectbox/Selectbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import {
  Tag,
  Search,
  Filter,
  X,
  Calendar,
  CheckCircle2,
  XCircle,
  Percent,
  DollarSign,
  Sparkles,
} from "lucide-react";
import { Badge } from "../ui/badge";

function Copuns() {
  const { t } = useTranslation();
  const {
    filterCoupons,
    couponsLoading,
    fetchFilterCouponsFunction,
    CouponPagination,
    setCouponPagination,
  } = indexStore();

  // 🧩 State للفلاتر
  const [filters, setFilters] = useState({
    code: "",
    is_active: "",
    type: "",
    starts_at: null,
    expires_at: null,
  });

  // 📥 جلب البيانات مع الفلاتر والصفحة الحالية
  const fetchCoupons = async (
    page = CouponPagination.current_page,
    filters
  ) => {
    const data = {
      page,
      per_page: CouponPagination.per_page,
      ...filters,
    };
    await fetchFilterCouponsFunction(data);
  };

  // 📜 عند تغيير الصفحة
  const handlePageChange = (page) => {
    setCouponPagination({ ...CouponPagination, current_page: page });
    fetchCoupons(page, filters);
  };

  // ⚙️ عند تحميل الصفحة لأول مرة
  useEffect(() => {
    fetchCoupons(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🧮 عند تغيير أحد الفلاتر
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // 🔍 تطبيق الفلاتر يدويًا (اختياري)
  const handleApplyFilters = () => {
    fetchCoupons(1, filters);
  };

  // 🧹 تصفية الفلاتر
  const handleClearFilters = () => {
    const five = {
      code: "",
      is_active: "",
      type: "",
      starts_at: null,
      expires_at: null,
    };
    setFilters(five);
    setTimeout(() => {
      fetchCoupons(1, five);
    }, 0);
  };
  const handleDateChange = (name, date) => {
    setFilters({
      ...filters,
      [name]: date ? format(date, "yyyy-MM-dd") : null, // ⚠️ صيغة التاريخ المناسبة للـ backend
    });
  };

  // Check if any filters are active
  const hasActiveFilters = 
    filters.code !== "" ||
    filters.is_active !== "" ||
    filters.type !== "" ||
    filters.starts_at !== null ||
    filters.expires_at !== null;

  return (
    <div className="flex flex-col h-full gap-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Tag className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">{t("gloabl.copuns")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("gloabl.manage_coupons") || "Manage and organize your coupons"}
            </p>
          </div>
        </div>
        <AddCouponsModel />
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-muted-foreground" />
              <CardTitle>{t("gloabl.filters") || "Filters"}</CardTitle>
            </div>
            {hasActiveFilters && (
              <Badge variant="secondary" className="gap-1">
                <span>{t("gloabl.active_filters") || "Active"}</span>
                <X className="w-3 h-3" />
              </Badge>
            )}
          </div>
          <CardDescription>
            {t("gloabl.filter_coupons") || "Filter coupons by code, status, type, or date range"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Code Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Search className="w-4 h-4" />
                {t("gloabl.code")}
              </label>
              <Input
                type="text"
                name="code"
                value={filters.code}
                onChange={handleFilterChange}
                placeholder={t("gloabl.code") || "Coupon code"}
                className="w-full"
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                {filters.is_active === "1" ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : filters.is_active === "0" ? (
                  <XCircle className="w-4 h-4 text-red-600" />
                ) : (
                  <Filter className="w-4 h-4" />
                )}
                {t("gloabl.status")}
              </label>
              <SelectBox
                width="w-full"
                data={[
                  { label: t("gloabl.active"), value: "1" },
                  { label: t("gloabl.inactive"), value: "0" },
                ]}
                onValueChange={(value) =>
                  handleFilterChange({ target: { name: "is_active", value: value } })
                }
                value={filters.is_active}
                placeholder={t("gloabl.all_status") || "All Status"}
                label="label"
                valueKey="value"
              />
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                {filters.type === "percentage" ? (
                  <Percent className="w-4 h-4 text-blue-600" />
                ) : filters.type === "fixed" ? (
                  <DollarSign className="w-4 h-4 text-amber-600" />
                ) : (
                  <Tag className="w-4 h-4" />
                )}
                {t("gloabl.type")}
              </label>
              <SelectBox
                width="w-full"
                data={[
                  { label: t("gloabl.fixed"), value: "fixed" },
                  { label: t("gloabl.percentage"), value: "percentage" },
                ]}
                onValueChange={(value) =>
                  handleFilterChange({
                    target: { name: "type", value: value },
                  })
                }
                value={filters.type}
                placeholder={t("gloabl.all_types") || "All Types"}
                label="label"
                valueKey="value"
              />
            </div>

            {/* Start Date Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t("gloabl.starts_at")}
              </label>
              <DatePicker
                date={filters.starts_at ? new Date(filters.starts_at) : null}
                setDate={(date) => handleDateChange("starts_at", date)}
                placeholder={t("gloabl.starts_at") || "Start date"}
              />
            </div>

            {/* End Date Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t("gloabl.expires_at")}
              </label>
              <DatePicker
                date={filters.expires_at ? new Date(filters.expires_at) : null}
                setDate={(date) => handleDateChange("expires_at", date)}
                placeholder={t("gloabl.expires_at") || "End date"}
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              {t("gloabl.clear")}
            </Button>
            <Button onClick={handleApplyFilters} className="gap-2">
              <Search className="w-4 h-4" />
              {t("gloabl.apply")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      <div className="flex-1">
        <Visibility visible={!couponsLoading && filterCoupons.length > 0}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">
                  {t("gloabl.coupons_found") || "Coupons"} ({filterCoupons.length})
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <Iterator
                data={filterCoupons}
                render={(e, i) => <CouponCard element={e} key={i} />}
              />
            </div>
          </div>
        </Visibility>

        <Visibility visible={couponsLoading}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Iterator
              data={[1, 2, 3, 4, 5, 6]}
              render={(e, i) => <CouponCardSkeleton key={i} />}
            />
          </div>
        </Visibility>

        <Visibility visible={!couponsLoading && filterCoupons.length === 0}>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Tag className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("gloabl.no_coupons_found") || "No coupons found"}
              </h3>
              <p className="text-sm text-muted-foreground text-center mb-4">
                {t("gloabl.no_coupons_description") || "Try adjusting your filters or create a new coupon"}
              </p>
              <AddCouponsModel />
            </CardContent>
          </Card>
        </Visibility>

        {/* Pagination */}
        <Visibility visible={!couponsLoading && filterCoupons.length > 0}>
          <div className="mt-6">
            <PaginationComponent
              pagination={CouponPagination}
              onPageChange={handlePageChange}
            />
          </div>
        </Visibility>
      </div>
    </div>
  );
}

export default Copuns;
